/**
 * Same COP team-chat thread the Navigator phones use (`#general`).
 * Mission Live talks here so the operator and the vehicle share one room.
 */
import { authorizedFetch } from './authorizedFetch';
import { withSseAccessToken } from '../utils/sseAuthUrl';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

export interface CrewChatMessage {
  id: number;
  channel: string;
  user_email: string;
  user_name: string;
  message: string;
  message_type: 'text' | 'alert' | 'system' | 'image';
  is_edited: boolean;
  created_at: string;
  attachment_url?: string | null;
  attachment_name?: string | null;
}

export interface CrewChatHistory {
  messages: CrewChatMessage[];
  count: number;
  has_more: boolean;
}

function chatUrl(path: string): string {
  const root = baseUrl.replace(/\/$/, '');
  return `${root}${path.startsWith('/') ? path : `/${path}`}`;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getCrewMessages(
  channel = 'general',
  limit = 50,
): Promise<CrewChatHistory> {
  const params = new URLSearchParams({ channel, limit: String(limit) });
  const res = await authorizedFetch(chatUrl(`/cop-team-chat/messages?${params}`), {
    headers: { 'Content-Type': 'application/json' },
  });
  return readJson<CrewChatHistory>(res);
}

export async function sendCrewMessage(
  message: string,
  channel = 'general',
  messageType: CrewChatMessage['message_type'] = 'text',
): Promise<CrewChatMessage> {
  const res = await authorizedFetch(chatUrl('/cop-team-chat/messages'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, channel, message_type: messageType }),
  });
  return readJson<CrewChatMessage>(res);
}

export async function sendCrewImage(
  file: File,
  channel = 'general',
  caption = '',
): Promise<CrewChatMessage> {
  const form = new FormData();
  form.append('channel', channel);
  form.append('caption', caption);
  form.append('file', file);
  const res = await authorizedFetch(chatUrl('/cop-team-chat/messages/image'), {
    method: 'POST',
    body: form,
  });
  return readJson<CrewChatMessage>(res);
}

export async function getCrewMediaObjectUrl(attachmentUrl: string): Promise<string> {
  const url = attachmentUrl.startsWith('http') ? attachmentUrl : attachmentUrl;
  const res = await authorizedFetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function connectCrewChatStream(
  channel: string,
  handlers: {
    onMessage: (msg: CrewChatMessage) => void;
    onEdited?: (msg: CrewChatMessage) => void;
    onDeleted?: (data: { id: number; channel: string }) => void;
    onOpen?: () => void;
    onError?: () => void;
  },
): EventSource {
  const es = new EventSource(
    withSseAccessToken(chatUrl(`/cop-team-chat/stream?channel=${encodeURIComponent(channel)}`)),
  );
  es.addEventListener('connected', () => handlers.onOpen?.());
  es.addEventListener('heartbeat', () => handlers.onOpen?.());
  es.addEventListener('new_message', (e) => {
    try { handlers.onMessage(JSON.parse(e.data)); } catch { /* ignore */ }
  });
  if (handlers.onEdited) {
    es.addEventListener('message_edited', (e) => {
      try { handlers.onEdited?.(JSON.parse(e.data)); } catch { /* ignore */ }
    });
  }
  if (handlers.onDeleted) {
    es.addEventListener('message_deleted', (e) => {
      try { handlers.onDeleted?.(JSON.parse(e.data)); } catch { /* ignore */ }
    });
  }
  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) handlers.onError?.();
  };
  return es;
}
